import styled from "@emotion/styled";
import { Button, Divider, Menu, MenuItem, type PaperProps } from "@mui/material";
import { useCallback, useState } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { InputNumber } from "../forms/index.ts";
import { Icon } from "../icons/index.ts";
import { MAX_ZOOM, MIN_ZOOM } from "./constants.ts";
import type { ZoomUnaryIncrement } from "./ZoomContext.ts";

const MIN_ZOOM_PERCENT = Math.round(MIN_ZOOM * 100);
const MAX_ZOOM_PERCENT = Math.round(MAX_ZOOM * 100);

const PerspectiveControlsStyler = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 80px;
  margin-inline-start: auto;

  pointer-events: ${({ active }) => (active ? "auto" : "none")};

  opacity: ${({ active }) => (active ? 1 : 0.25)};

  transition: opacity 300ms ease-in-out;
`;

const ZoomMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 4px;
  margin: 4px;
  border: none;
  border-radius: 4px;

  color: ${({ theme }) => theme.generalTextIcon};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSizeSmall};
`;

const ZoomInputRow = styled.div`
  display: flex;
  align-items: center;

  padding: 0 16px 8px;
  width: 160px;

  gap: 16px;

  & fieldset {
    border: none;
  }
`;

const ZoomInput = styled(InputNumber)`
  input {
    text-align: center;
  }
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 4px;
`;

const asPercent = (zoom: number) => Math.round(zoom * 100);

const menuPaperProps = { "data-test": "zoom-menu-container" } as PaperProps;

type PerspectiveControlsProps = {
  active?: boolean;
  zoom: number;
  onZoomStep: (delta: ZoomUnaryIncrement) => void;
  onZoomChange: (zoom: number) => void;
  onReset: () => void;
  /** Omitted by canvases that have no notion of fitting their content to the view. */
  onZoomToFit?: () => void;
};

export const PerspectiveControls = ({
  active = true,
  zoom,
  onZoomStep,
  onZoomChange,
  onReset,
  onZoomToFit,
  ...otherProps
}: PerspectiveControlsProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const zoomPercent = asPercent(zoom);

  const closeMenu = useCallback(() => setAnchorElement(null), []);

  const handlePercentCommitted = useCallback(
    (percent?: number) => {
      if (percent !== undefined) onZoomChange(percent / 100);
    },
    [onZoomChange],
  );

  const runAndClose = useCallback(
    (action: () => void) => () => {
      action();
      closeMenu();
    },
    [closeMenu],
  );

  return (
    <PerspectiveControlsStyler active={active} {...otherProps} data-test="perspective-controls-container">
      <ZoomMenuButton
        variant="outlined"
        color="inherit"
        aria-haspopup="menu"
        aria-expanded={anchorElement ? "true" : undefined}
        onClick={(event) => setAnchorElement(event.currentTarget)}
        data-test="zoom-menu-button"
      >
        {formatMessage("percent", { percent: zoomPercent })}
        <Icon icon="angle-down" size={10} />
      </ZoomMenuButton>

      <Menu
        open={!!anchorElement}
        anchorEl={anchorElement}
        onClose={closeMenu}
        autoFocus={false}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: menuPaperProps }}
      >
        {/* The menu list steals printable keys for type-ahead and arrows for navigation. */}
        <ZoomInputRow onKeyDown={(event) => event.stopPropagation()}>
          {formatMessage("customZoomLabel")}
          <ZoomInput
            autoFocus
            clampToRange
            min={MIN_ZOOM_PERCENT}
            max={MAX_ZOOM_PERCENT}
            value={zoomPercent}
            emptyValue={zoomPercent}
            onChangeCommitted={handlePercentCommitted}
            slotProps={{
              input: {
                endAdornment: "%",
              },
            }}
            dataTest="zoomPercent"
          />
        </ZoomInputRow>

        <StyledDivider />

        <MenuItem onClick={runAndClose(() => onZoomStep(1))} disabled={zoom >= MAX_ZOOM} data-test="zoom-in-button">
          {formatMessage("zoomIn")}
        </MenuItem>

        <MenuItem onClick={runAndClose(() => onZoomStep(-1))} disabled={zoom <= MIN_ZOOM} data-test="zoom-out-button">
          {formatMessage("zoomOut")}
        </MenuItem>

        <MenuItem onClick={runAndClose(onReset)} data-test="reset-button">
          {formatMessage("reset")}
        </MenuItem>

        {onZoomToFit && (
          <MenuItem onClick={runAndClose(onZoomToFit)} data-test="zoom-to-fit-button">
            {formatMessage("zoomToFit")}
          </MenuItem>
        )}

        <MenuItem onClick={runAndClose(() => onZoomChange(0.5))} data-test="zoom-to-50-button">
          {formatMessage("zoomTo", { percent: 50 })}
        </MenuItem>

        <MenuItem onClick={runAndClose(() => onZoomChange(1))} data-test="zoom-to-100-button">
          {formatMessage("zoomTo", { percent: 100 })}
        </MenuItem>

        <MenuItem onClick={runAndClose(() => onZoomChange(1.5))} data-test="zoom-to-150-button">
          {formatMessage("zoomTo", { percent: 150 })}
        </MenuItem>
      </Menu>
    </PerspectiveControlsStyler>
  );
};

const formatStrings = {
  customZoomLabel: {
    en: "Custom",
  },
  percent: {
    en: "{percent, number}%",
  },
  zoomIn: {
    en: "Zoom In",
  },
  zoomOut: {
    en: "Zoom Out",
  },
  reset: {
    en: "Reset Zoom & Pan",
  },
  zoomToFit: {
    en: "Zoom to Fit",
  },
  zoomTo: {
    en: "Zoom to {percent, number}%",
  },
} satisfies FormatMessages;
